'use client';

import FaIcon, { faChevronRight, faXmark } from '@/components/icon/FaIcon/FaIcon';
import NextImageView from '@/components/ui/NextImageView/NextImageView';
import { ImageType, type Product } from '@/types';
import { getDisplayDataFromProduct } from '@/utils/product';
import { memo, useEffect, useMemo } from 'react';
import styles from './ItemFilterModal.module.scss';

function ItemFilterModal({
  isOpen,
  onClose,
  products,
  selectedProductIds,
  onToggleProduct,
}: {
  isOpen: boolean;
  onClose: () => void;
  products: Product[];
  selectedProductIds: number[];
  onToggleProduct: (productId: number) => void;
}) {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = 'auto';
      };
    }
  }, [isOpen]);

  const displayDataList = useMemo(
    () => products.map((product, index) => getDisplayDataFromProduct(product, index, ImageType.icon)),
    [products],
  );

  if (!isOpen) return null;

  const handleOverlayClick = () => {
    onClose();
  };

  const handleModalBoxClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  return (
    <>
      <div className={styles.overlay} onClick={handleOverlayClick} aria-label='閉じる' />
      {/* biome-ignore lint/a11y/useSemanticElements: <explanation> */}
      <div className={styles.modalBox} onClick={handleModalBoxClick} role='dialog' aria-modal='true'>
        <div className={styles.modalHeader} onClick={onClose} aria-label='閉じる'>
          <button type='button' className={styles.modalClose}>
            <FaIcon icon={faXmark} />
          </button>
        </div>
        <ul className={styles.productList}>
          {displayDataList.map((displayData, index) => {
            const product = products[index];
            const isSelected = selectedProductIds.includes(product.id);
            return (
              <li key={product.id}>
                <button
                  type='button'
                  className={`${styles.productRow} ${isSelected ? styles.productRowSelected : ''}`}
                  onClick={() => onToggleProduct(product.id)}
                >
                  <div className={`${styles.productIconWrapper} ${styles[`productColor${displayData.color}`]}`}>
                    <NextImageView
                      src={displayData.iconSrc}
                      alt={displayData.alt}
                      fill={false}
                      width={40}
                      height={40}
                      objectFit='contain'
                      className={styles.productIcon}
                    />
                  </div>
                  <span className={styles.productName}>{product.name}</span>
                  <span className={styles.chevron}>
                    <FaIcon icon={faChevronRight} />
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </>
  );
}

export default memo(ItemFilterModal);
